import React from 'react';

import AlertBanner from '~/components/core/AlertBanner';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SortableTable from '~/components/core/Tables/SortableTable';
import Text from '~/components/core/TextComponents/Text';

import type { AiClaimImportPreviewByClaimsResponse, SubOrgPreviewRow } from '../types';

interface ClaimPreviewStepProps {
  previewData: AiClaimImportPreviewByClaimsResponse | null;
  isLoading: boolean;
  isStarting: boolean;
}

const ClaimPreviewStep: React.FC<ClaimPreviewStepProps> = ({ previewData, isLoading, isStarting }) => {
  const columns = [
    { id: 'name', label: 'Sub-Org Name' },
    { id: 'external_id', label: 'External ID' },
    { id: 'claims_count', label: 'Claims' },
    { id: 'documents_count', label: 'Documents' },
    { id: 'communications_count', label: 'Communications' },
    { id: 'notes_count', label: 'Notes' },
    {
      id: 'predicted_cost',
      label: 'Predicted Cost',
      specialCell: (row: SubOrgPreviewRow) =>
        row.predicted_cost !== null && row.predicted_cost !== undefined ? `$${row.predicted_cost.toFixed(2)}` : '-',
    },
    {
      id: 'is_ai_enabled',
      label: 'AI Enabled',
      specialCell: (row: SubOrgPreviewRow) => (
        <Text
          variant={Text.VARIANTS.XS}
          colorVariant={row.is_ai_enabled ? Text.COLOR_VARIANTS.PRIMARY : Text.COLOR_VARIANTS.ERROR}
        >
          {row.is_ai_enabled ? 'Yes' : 'No'}
        </Text>
      ),
    },
  ];

  const NO_SUB_ORG_SENTINEL_ID = -1;

  const rows = (previewData?.preview_rows || []).map((row) => ({
    ...row,
    id: row.sub_organization_id ?? NO_SUB_ORG_SENTINEL_ID,
  }));

  const greyedOutRowsIds = rows.filter((row) => !row.is_ai_enabled).map((row) => row.id);

  return (
    <LoadingSwitch isLoading={isLoading || isStarting} isError={false}>
      <div className="flex w-full flex-col gap-12">
        {previewData && previewData.not_found_claims.length > 0 && (
          <AlertBanner
            alertType={AlertBanner.ALERT_TYPES.INFO}
            note={`${previewData.not_found_claims.length} claim numbers were not found: ${previewData.not_found_claims.map((c) => c.claim_id_display).join(', ')}`}
          />
        )}

        {previewData && previewData.closed_claims.length > 0 && (
          <AlertBanner
            alertType={AlertBanner.ALERT_TYPES.INFO}
            note={`${previewData.closed_claims.length} closed claims will be imported: ${previewData.closed_claims.map((c) => c.claim_id_display).join(', ')}`}
          />
        )}

        {previewData && previewData.fully_processed_claims.length > 0 && (
          <AlertBanner
            alertType={AlertBanner.ALERT_TYPES.INFO}
            note={`${previewData.fully_processed_claims.length} claims have already been fully processed and will be skipped: ${previewData.fully_processed_claims.map((c) => c.claim_id_display).join(', ')}`}
          />
        )}

        {previewData && previewData.non_ai_enabled_claims.length > 0 && (
          <AlertBanner
            alertType={AlertBanner.ALERT_TYPES.INFO}
            note={`${previewData.non_ai_enabled_claims.length} claims belong to sub-organizations where AI is not enabled and will not be imported: ${previewData.non_ai_enabled_claims.map((c) => c.claim_id_display).join(', ')}`}
          />
        )}

        <SortableTable columns={columns} rows={rows} stickyHeader greyedOutRowsIds={greyedOutRowsIds} />

        {previewData && (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            Total processable claims: {previewData.total_processable_claims}
          </Text>
        )}
      </div>
    </LoadingSwitch>
  );
};

export default ClaimPreviewStep;
